import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isWeekend,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { JobStatus } from "@/shared/types";
import { normalizeLegacyStatus } from "@/shared/types";
import type { PublicHoliday } from "@/hooks/usePublicHolidays";
import { getHolidayForDate } from "@/hooks/usePublicHolidays";

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  primaryColor?: string;
}

interface CalendarEvent {
  id: string;
  jobNumber?: string;
  title: string;
  date: Date;
  scheduledStartDate?: Date;
  scheduledEndDate?: Date;
  status: JobStatus;
  customer: string;
  address?: string;
  assignedStaff?: StaffMember[];
  duration?: number; // Duration in hours
}

interface MonthViewProps {
  date: Date;
  events: CalendarEvent[];
  staff: StaffMember[];
  holidays?: PublicHoliday[];
  onChangeDate: (newDate: Date) => void;
  onOpenJob: (jobId: string) => void;
  onDayClick?: (date: Date) => void;
}

const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const statusColors: Record<JobStatus, { bg: string; border: string; text: string; dot: string }> = {
  "quote-needed": { 
    bg: "bg-red-50 dark:bg-red-950/40", 
    border: "border-l-red-500",
    text: "text-red-700 dark:text-red-300", 
    dot: "bg-red-500" 
  },
  "quoted": { 
    bg: "bg-orange-50 dark:bg-orange-950/40", 
    border: "border-l-orange-500",
    text: "text-orange-700 dark:text-orange-300", 
    dot: "bg-orange-500" 
  },
  "accepted": { 
    bg: "bg-green-50 dark:bg-green-950/40", 
    border: "border-l-green-500",
    text: "text-green-700 dark:text-green-300", 
    dot: "bg-green-500" 
  },
  "in-progress": { 
    bg: "bg-blue-50 dark:bg-blue-950/40", 
    border: "border-l-blue-500",
    text: "text-blue-700 dark:text-blue-300", 
    dot: "bg-blue-500" 
  },
  "complete": { 
    bg: "bg-indigo-50 dark:bg-indigo-950/40", 
    border: "border-l-indigo-600",
    text: "text-indigo-700 dark:text-indigo-300", 
    dot: "bg-indigo-600" 
  },
  "invoiced": { 
    bg: "bg-purple-50 dark:bg-purple-950/40", 
    border: "border-l-purple-500",
    text: "text-purple-700 dark:text-purple-300", 
    dot: "bg-purple-500" 
  },
  "paid": { 
    bg: "bg-pink-50 dark:bg-pink-950/40", 
    border: "border-l-pink-500",
    text: "text-pink-700 dark:text-pink-300", 
    dot: "bg-pink-500" 
  },
  "archived": { 
    bg: "bg-slate-100 dark:bg-slate-800/60", 
    border: "border-l-slate-400",
    text: "text-slate-600 dark:text-slate-400", 
    dot: "bg-slate-400" 
  },
};

export function MonthView({
  date,
  events,
  staff,
  holidays = [],
  onChangeDate,
  onOpenJob,
  onDayClick,
}: MonthViewProps) {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  
  const getEventsForDay = (day: Date): CalendarEvent[] => {
    return events.filter((event) => {
      const eventStart = event.scheduledStartDate || event.date;
      const eventEnd = event.scheduledEndDate || event.date;
      
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);
      
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });
  };

  const isToday = (day: Date) => isSameDay(day, new Date());
  const isCurrentMonth = (day: Date) => isSameMonth(day, date);

  return (
    <div className="w-full h-full flex flex-col bg-background overflow-hidden rounded-lg">
      {/* Month Navigation Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onChangeDate(subMonths(date, 1))}
          data-testid="button-prev-month"
          className="h-8 w-8 rounded-full"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          {format(date, "MMMM yyyy")}
        </h2>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChangeDate(new Date())}
            data-testid="button-today"
            className="h-8 text-xs font-medium"
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChangeDate(addMonths(date, 1))}
            data-testid="button-next-month"
            className="h-8 w-8 rounded-full"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 border-b border-border">
        {dayLabels.map((day, index) => (
          <div
            key={day}
            className={`
              py-2.5 text-center text-xs font-medium uppercase tracking-wider
              ${index >= 5 ? "text-muted-foreground/70" : "text-muted-foreground"}
            `}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-7 h-full" style={{ minHeight: "100%" }}>
          {days.map((day) => {
            const dayEvents = getEventsForDay(day);
            const isToday_ = isToday(day);
            const isCurrentMonth_ = isCurrentMonth(day);
            const isWeekend_ = isWeekend(day);
            const holiday = getHolidayForDate(holidays, day);

            return (
              <div
                key={day.toISOString()}
                className={`
                  min-h-[110px] p-2 flex flex-col border-b border-r border-border/50
                  transition-colors duration-150
                  ${isCurrentMonth_ 
                    ? isWeekend_ 
                      ? "bg-muted/20 dark:bg-muted/10" 
                      : "bg-background" 
                    : "bg-muted/40 dark:bg-muted/20"
                  }
                  ${isToday_ ? "bg-primary/5 dark:bg-primary/10" : ""}
                  ${onDayClick ? "cursor-pointer hover:bg-accent/50 dark:hover:bg-accent/30" : ""}
                `}
                data-testid={`day-cell-${format(day, "yyyy-MM-dd")}`}
                onClick={(e) => {
                  if (onDayClick && e.target === e.currentTarget) {
                    onDayClick(day);
                  }
                }}
              >
                {/* Day number */}
                <div
                  className={`
                    flex items-center justify-center w-7 h-7 mb-1 text-sm font-medium rounded-full
                    transition-colors cursor-pointer
                    ${isToday_ 
                      ? "bg-primary text-primary-foreground shadow-sm" 
                      : isCurrentMonth_ 
                        ? "text-foreground hover:bg-accent" 
                        : "text-muted-foreground/50"
                    }
                  `}
                  onClick={(e) => {
                    if (onDayClick) {
                      e.stopPropagation();
                      onDayClick(day);
                    }
                  }}
                >
                  {format(day, "d")}
                </div>

                {/* Holiday label */}
                {holiday && (
                  <div
                    className="text-[10px] leading-tight px-1.5 py-0.5 rounded truncate font-medium mb-0.5"
                    style={{ background: "#FEE2E2", color: "#991B1B", borderLeft: "2px solid #EF4444" }}
                    title={holiday.localName || holiday.name}
                  >
                    🏖️ {holiday.localName || holiday.name}
                  </div>
                )}

                {/* Events */}
                <div 
                  className="flex flex-col gap-0.5 flex-1 overflow-hidden"
                  onClick={(e) => {
                    if (onDayClick && e.target === e.currentTarget) {
                      e.stopPropagation();
                      onDayClick(day);
                    }
                  }}
                >
                  {dayEvents.slice(0, 3).map((event) => {
                    const normalizedStatus = normalizeLegacyStatus(event.status) as JobStatus;
                    const colors = statusColors[normalizedStatus] || statusColors["quote-needed"];
                    
                    // Format duration helper
                    const formatDuration = (hours: number): string => {
                      if (hours < 1) {
                        return `${Math.round(hours * 60)}m`;
                      } else if (hours === 1) {
                        return "1h";
                      } else if (hours < 24) {
                        const wholeHours = Math.floor(hours);
                        const minutes = Math.round((hours - wholeHours) * 60);
                        if (minutes === 0) {
                          return `${wholeHours}h`;
                        }
                        return `${wholeHours}h ${minutes}m`;
                      } else {
                        const days = Math.floor(hours / 24);
                        const remainingHours = Math.floor(hours % 24);
                        if (remainingHours === 0) {
                          return `${days}d`;
                        }
                        return `${days}d ${remainingHours}h`;
                      }
                    };

                    const duration = event.duration || 
                      (event.scheduledStartDate && event.scheduledEndDate
                        ? (event.scheduledEndDate.getTime() - event.scheduledStartDate.getTime()) / (1000 * 60 * 60)
                        : null);

                    return (
                      <div
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenJob(event.id);
                        }}
                        className={`
                          text-[11px] leading-tight px-2 py-1 rounded-md truncate cursor-pointer
                          border-l-2 transition-all duration-150
                          hover:shadow-sm hover:scale-[1.02]
                          ${colors?.bg} ${colors?.text} ${colors?.border}
                        `}
                        title={`${event.jobNumber ? event.jobNumber + ' - ' : ''}${event.title}${duration ? ` (${formatDuration(duration)})` : ''}`}
                        data-testid={`event-chip-${event.id}`}
                      >
                        {event.jobNumber && (
                          <span className="font-semibold">{event.jobNumber}</span>
                        )}
                        {event.jobNumber && " "}
                        <span className="opacity-90">{event.title}</span>
                        {duration && (
                          <span className="opacity-75 ml-1">• {formatDuration(duration)}</span>
                        )}
                      </div>
                    );
                  })}

                  {/* Show "+X more" if more than 3 events */}
                  {dayEvents.length > 3 && (
                    <div
                      className="text-[10px] px-2 py-0.5 text-muted-foreground font-medium hover:text-foreground cursor-pointer transition-colors"
                      data-testid={`event-more-${format(day, "yyyy-MM-dd")}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDayClick) onDayClick(day);
                      }}
                    >
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
